/* eslint-disable consistent-return */
/* eslint-disable array-callback-return */
import { useEffect, useRef, useMemo, useCallback, useState } from 'react'
import { toast } from 'react-toastify'

import Uppy from '@uppy/core'
import XHRUpload from '@uppy/xhr-upload'
import ExifReader from 'exifreader'

import { storageAccountName } from '@functions/api'

function sanitizeFileName(name) {
  return name
    .normalize('NFC')              // normalize Unicode
    .replace(/\u202F/g, ' ')       // replace narrow no-break space with space
    .replace(/[\u200B-\u200F\uFEFF]/g, '') // remove zero-width chars
    .replace(/[\\#?]/g, '_')       // replace invalid URL chars
    .replace(/[ \t]+$/g, '')       // trim trailing whitespace
    .replace(/\.+$/g, '')          // remove trailing dots
    .slice(0, 1024);               // enforce Azure limit
}

const initializeUppy = (options) => {
  const {
    allowedFileTypes,
    callbacks,
    currentUserId,
    fileServiceContainer,
    maxFileSize,
    onUploaderError,
    requestOptions = {},
  } = options

  const errorList = []
  const uploadedList = []

  const { sasToken, container, id: fileServiceContainerId } = fileServiceContainer || {}
  const { serviceJobId } = requestOptions

  const { toggleAddedResource } = callbacks

  const filePath = `${serviceJobId}/resources`
  const storageUrl = `https://${storageAccountName}.blob.core.windows.net/${container}/${filePath}`

  const processFile = async (file, uppy) => {
    const safeName = encodeURIComponent(sanitizeFileName(file?.meta?.name || file?.name))
    const blobUrl = `${storageUrl}/${safeName}`

    try {
      new URL(blobUrl)
    } catch {
      toast.error(`Invalid blob URL generated: ${blobUrl}`)
    }

    const endpoint = `${blobUrl}?${sasToken}`

    let tags = `userId=${currentUserId}&containerId=${fileServiceContainerId}`
    tags = `${tags}&resourceTypeId=${requestOptions.resourceTypeId}`

    uppy.setFileMeta(file.id, {
      ...requestOptions,
      fileServiceContainerId,
      filePath,
      fileName: file.meta.name,
      blobUrl,
    })

    if (file.meta.type.startsWith('image/')) {
      const arrayBuffer = await file.data.arrayBuffer()

      try {
        const metaKeys = [
          'ColorSpace',
          'DateTimeOriginal',
          'FNumber',
          'ExposureTime',
          'ImageLength',
          'ImageWidth',
          'ISOSpeedRatings',
          'Make',
          'Model',
          // jpg exif keys has spaces in between
          'Color Space',
          'Image Height',
          'Image Width',
        ]

        const exifTags = ExifReader.load(arrayBuffer)
        const filterExif = Object.fromEntries(
          metaKeys.map(key => [key, exifTags[key]]),
        )

        uppy.setFileState(file.id, {
          xhrUpload: {
            ...file.xhrUpload,
            endpoint: endpoint,
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'x-ms-blob-content-type': file.meta.type,
              'x-ms-meta-size': file.size,
              ...(filterExif?.DateTimeOriginal) && { 'x-ms-meta-datetimeoriginal': filterExif.DateTimeOriginal.description },
              ...(filterExif?.ExposureTime) && { 'x-ms-meta-exposuretime': `ISO ${filterExif.ExposureTime.description}` },
              ...(filterExif?.FNumber) && { 'x-ms-meta-fnumber': filterExif.FNumber.description },
              ...(filterExif?.ISOSpeedRatings) && { 'x-ms-meta-iso': filterExif.ISOSpeedRatings.description },
              ...(filterExif?.Make) && { 'x-ms-meta-make': filterExif.Make.description },
              ...(filterExif?.Model) && { 'x-ms-meta-model': filterExif.Model.description },
              ...(filterExif?.ColorSpace && !filterExif['Color Space']?.value) && { 'x-ms-meta-colorspace': filterExif.ColorSpace.description },
              ...(filterExif['Color Space']?.value) && { 'x-ms-meta-colorspace': filterExif['Color Space'].value },
              ...(filterExif['Image Height']?.value && filterExif['Image Width']?.value && !file.meta.type.endsWith('DNG'))
              && { 'x-ms-meta-dimensions': `${filterExif['Image Width'].value} x ${filterExif['Image Height'].value}` },
              ...(filterExif?.ImageLength && filterExif?.ImageWidth && !file.meta.type.endsWith('DNG')
                && (!filterExif['Image Height']?.value && !filterExif['Image Width']?.value))
              && { 'x-ms-meta-dimensions': `${filterExif.ImageWidth.value} x ${filterExif.ImageLength.value}` },
              'x-ms-tags': tags,
            },
          },
        })
      } catch (error) {
        console.error('Error parsing EXIF data:', error)

        uppy.setFileState(file.id, {
          xhrUpload: {
            ...file.xhrUpload,
            endpoint: endpoint,
            headers: {
              'x-ms-blob-type': 'BlockBlob',
              'x-ms-blob-content-type': file.meta.type,
              'x-ms-tags': tags,
            },
          },
        })
      }
    } else {
      uppy.setFileState(file.id, {
        xhrUpload: {
          ...file.xhrUpload,
          endpoint: endpoint,
          headers: {
            'x-ms-blob-type': 'BlockBlob',
            'x-ms-blob-content-type': file.meta.type,
            'x-ms-tags': tags,
          },
        },
      })
    }
  }

  const uppy = new Uppy({
    id: `uppyUpload-${requestOptions.resourceTypeId}`,
    debug: false,
    autoProceed: false,
    restrictions: {
      allowedFileTypes,
      maxFileSize,
    },
  })
    .use(XHRUpload, {
      method: 'PUT',
      formData: false,
      fieldName: 'FormFile',
      getResponseError() { },
      timeout: 0,
    })
    .on('restriction-failed', (file, error) => {
      if (error.message === 'Cannot add more files') {
        toast.warning(error.message)
      } else {
        errorList.push({ ...file, errorType: 'restricted', errorMessage: error.message })
      }
    })
    .on('files-added', (files) => {
      Object.values(files).map(file => processFile(file, uppy))
      toggleAddedResource(requestOptions.resourceTypeId)
    })
    .on('upload-success', (file) => {
      uploadedList.push(file)
    })
    .on('upload-error', (file, error) => {
      const errorMessage = String(error?.message || '')
      const isNetworkIssue = (
        (typeof navigator !== 'undefined' && navigator.onLine === false)
        || /network|offline|failed to fetch|ecconn|net::/i.test(errorMessage)
      )

      if (isNetworkIssue) {
        if (typeof onUploaderError === 'function') {
          onUploaderError({
            error,
            message: 'Connection lost. Upload will resume automatically once reconnected.',
            resourceTypeId: requestOptions.resourceTypeId,
            isNetworkError: true,
          })
        }
      }

      errorList.push({ ...file, errorType: 'failed', errorMessage: 'Failed to Upload.' })
    })
    .on('error', (error) => {
      const errorMessage = 'Some files failed to upload. Please retry.'
      errorList.push({ errorType: 'error', errorMessage })

      if (typeof onUploaderError === 'function') {
        onUploaderError({
          error,
          message: errorMessage,
          resourceTypeId: requestOptions.resourceTypeId,
        })
      }
    })

  return uppy
}

const checkRequiredOptions = (options = []) => {
  const requiredOptions = ['allowedFileTypes', 'requestOptions', 'fileServiceContainer']

  return options.every(option => requiredOptions.every(key => Object.keys(option).includes(key)))
}

function useUppyUploadResource(options = []) {
  const uppyRefs = useRef([])
  const [uploaderError, setUploaderError] = useState(null)

  const handleUploaderError = useCallback((payload) => {
    if (!payload) {
      setUploaderError(null)
      return
    }

    setUploaderError((previous) => {
      if (
        previous?.message === payload?.message
        && previous?.resourceTypeId === payload?.resourceTypeId
      ) {
        return previous
      }

      return payload
    })
  }, [])

  const uppyInstances = useMemo(() => {
    if (!checkRequiredOptions(options)) return []

    return options.map(option => initializeUppy({
      ...option,
      onUploaderError: (payload) => {
        handleUploaderError(payload)
      },
    }))
  }, [options, handleUploaderError])

  uppyRefs.current = uppyInstances

  const clearLastError = useCallback(() => {
    setUploaderError(null)
  }, [])

  const retryAllUploads = useCallback(() => {
    let retried = false

    if (uppyRefs.current) {
      uppyRefs.current.forEach((uppy) => {
        if (uppy && typeof uppy.retryAll === 'function') {
          const files = uppy.getFiles?.() || []
          const hasRetryableFiles = files.some(file => file?.error || file?.progress?.uploadComplete === false)

          if (hasRetryableFiles) {
            uppy.retryAll()
            retried = true
          }
        }
      })
    }

    if (retried) {
      clearLastError()
    }

    return retried
  }, [clearLastError])

  useEffect(() => () => {
    uppyRefs.current.forEach(uppy => uppy?.close?.())
  }, [])

  return {
    instances: uppyRefs.current || [],
    lastError: uploaderError,
    retryAllUploads,
    clearLastError,
  }
}

export default useUppyUploadResource
